const SESSION_KEY = "x3algo.auth.session.v2";
const LOGOUT_KEY = "x3algo.auth.logout-pending.v2";
const SESSION_ID = /^[a-f0-9]{32}$/;
const REVISION = /^[A-Za-z0-9_-]{16,128}$/;

export function authAttemptError(code = "AUTH_ATTEMPT_STALE") {
  const error = new Error(
    code === "AUTH_LOGOUT_PENDING"
      ? "Server sign-out has not been confirmed. Sign in again or retry sign-out."
      : "This authentication attempt is no longer current.",
  );
  error.code = code;
  error.privateAuthFailure = true;
  return error;
}

export function parseAuthSession(value) {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).length !== 2 ||
    value.schema !== 2 ||
    typeof value.id !== "string" ||
    !SESSION_ID.test(value.id)
  )
    return null;
  return { schema: 2, id: value.id };
}

// This coordinates browser intent only. Server admission remains authoritative.
export function createAuthAttemptState({ storage, eventTarget, createId }) {
  let generation = 0;
  let storageFailed = false;
  let disposed = false;
  const subscribers = new Set();

  const read = (key) => {
    try {
      return storage().getItem(key);
    } catch (_error) {
      storageFailed = true;
      return null;
    }
  };
  const write = (key, value) => {
    try {
      if (value === null) storage().removeItem(key);
      else storage().setItem(key, value);
    } catch (_error) {
      storageFailed = true;
    }
  };
  const parse = (raw) => {
    if (raw === null) return null;
    if (typeof raw !== "string" || raw.length > 512) return undefined;
    try {
      return JSON.parse(raw);
    } catch (_error) {
      return undefined;
    }
  };
  const parseMarker = (raw) => {
    if (raw === null) return null;
    const value = parse(raw);
    if (
      !value ||
      typeof value !== "object" ||
      Array.isArray(value) ||
      Object.keys(value).length !== 3 ||
      value.schema !== 1 ||
      typeof value.revision !== "string" ||
      !REVISION.test(value.revision) ||
      (value.blockedSid !== null &&
        (typeof value.blockedSid !== "string" ||
          !SESSION_ID.test(value.blockedSid)))
    ) {
      return {
        schema: 1,
        revision: "invalid-persisted-marker",
        blockedSid: null,
      };
    }
    return value;
  };

  let markerRaw = read(LOGOUT_KEY);
  let sessionRaw = read(SESSION_KEY);
  let marker = parseMarker(markerRaw);
  let session = parseAuthSession(parse(sessionRaw));
  let blocked = Boolean(marker) || storageFailed;

  const status = () => ({
    blocked,
    logoutPending: Boolean(marker),
    storageAvailable: !storageFailed,
  });
  const publish = (kind) => {
    const change = { kind, ...status() };
    for (const subscriber of subscribers) subscriber(change);
  };
  const sync = () => {
    if (disposed || storageFailed) return;
    const nextMarkerRaw = read(LOGOUT_KEY);
    const nextSessionRaw = read(SESSION_KEY);
    if (storageFailed) {
      generation++;
      blocked = true;
      publish("storage-unavailable");
      return;
    }
    if (nextMarkerRaw === markerRaw && nextSessionRaw === sessionRaw) return;
    markerRaw = nextMarkerRaw;
    sessionRaw = nextSessionRaw;
    marker = parseMarker(markerRaw);
    session = parseAuthSession(parse(sessionRaw));
    blocked = Boolean(marker);
    generation++;
    publish("peer-change");
  };
  const capture = () => {
    sync();
    return Object.freeze({
      generation,
      markerRevision: marker?.revision ?? null,
    });
  };
  const isCurrent = (attempt) => {
    sync();
    return !disposed && Boolean(attempt) && attempt.generation === generation;
  };
  const assertCurrent = (attempt) => {
    if (!isCurrent(attempt)) throw authAttemptError();
  };
  const saveSession = (value) => {
    session = parseAuthSession(value);
    sessionRaw = session ? JSON.stringify(session) : null;
    write(SESSION_KEY, sessionRaw);
  };
  const clearMarker = () => {
    marker = null;
    markerRaw = null;
    write(LOGOUT_KEY, null);
  };
  const onStorage = (event) => {
    if (event.key !== null && ![SESSION_KEY, LOGOUT_KEY].includes(event.key))
      return;
    if (event.storageArea) {
      try {
        if (event.storageArea !== storage()) return;
      } catch (_error) {
        /* sync fails closed */
      }
    }
    sync();
  };
  eventTarget?.addEventListener("storage", onStorage);

  return {
    capture,
    isCurrent,
    assertCurrent,
    getStatus() {
      sync();
      return status();
    },
    beginAuthentication() {
      sync();
      generation++;
      publish("authentication-start");
      return capture();
    },
    beginLogout() {
      sync();
      const revision = createId();
      if (typeof revision !== "string" || !REVISION.test(revision))
        throw authAttemptError();
      generation++;
      marker = Object.freeze({
        schema: 1,
        revision,
        blockedSid: session?.id ?? null,
      });
      markerRaw = JSON.stringify(marker);
      write(LOGOUT_KEY, markerRaw);
      blocked = true;
      publish("logout-start");
      return capture();
    },
    finishLogout(attempt, serverRevoked) {
      if (!isCurrent(attempt)) return false;
      generation++;
      if (serverRevoked === true) clearMarker();
      // Keep this mounted page signed out even after acknowledged revocation.
      blocked = true;
      publish(
        serverRevoked === true ? "logout-confirmed" : "logout-unconfirmed",
      );
      return true;
    },
    acceptAuthentication(attempt, value) {
      assertCurrent(attempt);
      saveSession(value);
      clearMarker();
      blocked = false;
      publish("authenticated");
      return capture();
    },
    acceptCurrentSession(attempt, value) {
      assertCurrent(attempt);
      if (attempt.markerRevision !== (marker?.revision ?? null))
        throw authAttemptError();
      const admitted = parseAuthSession(value);
      if (blocked) {
        if (
          !marker?.blockedSid ||
          !admitted ||
          admitted.id === marker.blockedSid
        ) {
          throw authAttemptError("AUTH_LOGOUT_PENDING");
        }
        clearMarker();
        blocked = false;
      }
      saveSession(admitted);
      return capture();
    },
    rememberSession(attempt, value) {
      assertCurrent(attempt);
      if (blocked) throw authAttemptError("AUTH_LOGOUT_PENDING");
      saveSession(value);
      return capture();
    },
    invalidate(kind = "invalidated") {
      generation++;
      publish(kind);
      return capture();
    },
    requireReauthentication() {
      generation++;
      blocked = true;
      publish("reauthentication");
      return capture();
    },
    subscribe(subscriber) {
      subscribers.add(subscriber);
      return () => subscribers.delete(subscriber);
    },
    dispose() {
      disposed = true;
      generation++;
      subscribers.clear();
      eventTarget?.removeEventListener("storage", onStorage);
    },
  };
}
